import { forEach, toArray, isElement, merge, uniqueId } from 'lodash';
import {
	dispatchCustomEvent,
	publish,
	subscribe
} from '../../01-helpers/event-manager';
import { DROPDOWN_ } from '../../02-const/id-names';
import { DROPDOWN_CLICKED, TOGGLE_CLOSE } from '../../02-const/event-names';

const moduleName = 'toggle',
	attributeNames = {
		toggleName: `data-${moduleName}-name`
	},
	eventNames = {
		afterClose: 'onafterclose',
		afterOpen: 'onafteropen'
	},
	selectors = {
		base: `.js-${moduleName}`,
		content: `.js-${moduleName}__content`,
		trigger: `.js-${moduleName}__trigger`
	},
	defaultConfig = {
		cssClasses: {
			closedClass: 'is-closed',
			openClass: 'is-open'
		}
	};

/* ============= TOGGLE METHODS ============== */

function getToggleParts(element) {
	const toggleName = getToggleName(element);
	if (toggleName === null) {
		return getTogglePartsFromContainer(element);
	} else {
		return getTogglePartsForName(toggleName);
	}
}

function getToggleName(element) {
	if (element.hasAttribute(attributeNames.toggleName)) {
		return element.getAttribute(attributeNames.toggleName);
	}
	return null;
}

function getTogglePartsFromContainer(element) {
	const trigger = element.querySelector(selectors.trigger),
		content = element.querySelector(selectors.content);

	return {
		triggers: trigger === null ? [] : [trigger],
		content
	};
}

function getTogglePartsForName(toggleName) {
	const triggers = document.querySelectorAll(
			`${selectors.trigger}[${attributeNamespace.toggleName}="${toggleName}"]`
		),
		content = document.querySelector(
			`${selectors.content}[${attributeNames.toggleName}="${toggleName}"]`
		);

	return {
		triggers: toArray(triggers),
		content
	};
}

/* ============= TOGGLE METHODS ============== */

function bindEvents() {
	// Since event listeners change the scope of 'this', we need to make
	// sure we are able to remove this event, so we define it before adding.
	this.triggerEventHandler = (event) => this._onTriggerEventHandler(event);
	forEach(this.triggers, (trigger) => {
		trigger.addEventListener('click', (evt) => {
			this.triggerEventHandler(evt);
		});
	});

	subscribe(DROPDOWN_CLICKED, closeToggle.bind(this));
	subscribe(TOGGLE_CLOSE, closeAll.bind(this));
}

function closeAll() {
	forEach(this.triggers, (trigger) => {
		publish(DROPDOWN_CLICKED, { dropdownId: trigger.id });
	});
}

function closeToggle(evt) {
	if (evt.detail.dropdownId !== this.id && this.isOpen) {
		this._close();
	}
}

function updateUIState() {
	if (this.isOpen) {
		updateCssClass.call(this, 'remove', this.config.cssClasses.closedClass);
		updateCssClass.call(this, 'add', this.config.cssClasses.openClass);
	} else {
		updateCssClass.call(this, 'add', this.config.cssClasses.closedClass);
		updateCssClass.call(this, 'remove', this.config.cssClasses.openClass);
	}
}

function updateCssClass(methodName, className) {
	this.content.classList[methodName](className);
	forEach(this.triggers, (trigger) => trigger.classList[methodName](className));
}

export class Toggle {
	/**
	 * Constructor
	 *
	 * @constructor
	 * @param {any} triggers
	 * @param {any} content
	 * @param {any} config
	 */
	constructor(baseElement, config = {}) {
		if (!isElement(baseElement)) {
			return null;
		}

		const parts = getToggleParts(baseElement);
		if (!isElement(parts.content) || parts.triggers.length === 0) {
			return null;
		}

		this._configure(parts.triggers, parts.content, config);
		this._setup();
	}

	/* ========================================= */

	_configure(triggers, content, config) {
		this.id = uniqueId(DROPDOWN_);
		this.triggers = triggers;
		this.content = content;

		const mergedConfig = merge({}, defaultConfig, config);
		this.config = mergedConfig;
	}

	_setup() {
		bindEvents.call(this);
		this._close();
	}

	/* ============= PROTECTED METHODS ============== */
	_onTriggerEventHandler(evt) {
		evt.preventDefault();
		evt.stopPropagation();

		if (this.isOpen) {
			this._close();
		} else {
			publish(DROPDOWN_CLICKED, { dropdownId: this.id });
			this._open();
		}
	}

	_open() {
		if (this.open) {
			return;
		}
		this.isOpen = true;
		this._updateUIState();
		this._dispatchEvent(eventNames.afterOpen);
	}

	_close() {
		if (!this.isOpen) {
			return;
		}
		this.isOpen = false;
		this._updateUIState();
		this._dispatchEvent(eventNames.afterClose);
	}

	_updateUIState() {
		updateUIState.call(this);
	}

	_dispatchEvent(eventName, data, config) {
		const eventData = merge({}, data, { instance: this }),
			eventConfig = merge({ bubbles: false }, config);

		return dispatchCustomEvent(this.content, eventName, eventData, eventConfig);
	}
	/* ============= PROTECTED METHODS ============== */
}

const initToggle = () => {
	forEach(document.querySelectorAll(selectors.base), (toggleElement) => {
		const toggle = new Toggle(toggleElement);
	});
};

initToggle();
